import * as React from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ui/components/ui/alert-dialog'
import { buttonVariants } from '@ui/components/ui/button'
import { cn } from '@ui/lib/utils'

export interface ConfirmDialogProps {
  /** Steuert die Sichtbarkeit. */
  open: boolean
  /** Wird beim Schliessen aufgerufen. */
  onOpenChange: (open: boolean) => void
  /** Die Frage, kurz und konkret. */
  title: string
  /** Was passiert, wenn bestätigt wird — inklusive Folgen. */
  description: React.ReactNode
  /** Beschriftung der bestätigenden Aktion. Standard: "Bestätigen". */
  confirmLabel?: string
  /** Beschriftung des Abbruchs. Standard: "Abbrechen". */
  cancelLabel?: string
  /** Färbt die Bestätigung rot — für Löschen und Verwerfen. */
  destructive?: boolean
  /** Sperrt die Knöpfe, solange die Aktion läuft. */
  pending?: boolean
  /** Wird ausgeführt, wenn der Nutzer bestätigt. */
  onConfirm: () => void
}

/**
 * Rückfrage vor einer Aktion, die sich nicht ohne Weiteres rückgängig machen
 * lässt: Löschen, Verwerfen, Freigeben.
 *
 * @example
 * ```tsx
 * <ConfirmDialog
 *   open={deleteDialogOpen}
 *   onOpenChange={setDeleteDialogOpen}
 *   title="Vorgang löschen?"
 *   description="Der Vorgang wird dauerhaft entfernt. Das lässt sich nicht rückgängig machen."
 *   confirmLabel="Löschen"
 *   destructive
 *   onConfirm={() => deleteVorgang.mutate(id)}
 * />
 * ```
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  destructive = false,
  pending = false,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            className={cn(
              destructive &&
                buttonVariants({ variant: 'destructive' }).replace('shadow-xs', ''),
            )}
            onClick={(event) => {
              // Der Dialog schließt sonst, bevor die Aktion durch ist.
              event.preventDefault()
              onConfirm()
            }}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
